import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Archive, Clock, Camera, Award, Users, Globe, 
  BookOpen, Play, ArrowRight, Building, GraduationCap 
} from "lucide-react";

const heritageCards = [
  {
    icon: BookOpen,
    title: "Our Story",
    description: "From a UK foundation to a global presence, discover the journey that built M2L.",
    link: "/heritage/timeline",
    image: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=600",
  },
  {
    icon: Clock,
    title: "Brand Timeline",
    description: "Key milestones and moments that shaped our organization since 2013.",
    link: "/heritage/timeline",
    image: "https://images.unsplash.com/photo-1501139083538-0139583c060f?w=600",
  },
  {
    icon: Archive,
    title: "Legacy Archive",
    description: "Access historical documents, records, and institutional memory.",
    link: "/heritage/archive",
    image: "https://images.unsplash.com/photo-1568667256549-094345857637?w=600",
  },
  {
    icon: GraduationCap,
    title: "Past Programs",
    description: "Explore educational programs and initiatives from our history.",
    link: "/heritage/archive",
    image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600",
  },
  {
    icon: Camera,
    title: "Photo Gallery",
    description: "Visual memories from over a decade of student success stories.",
    link: "/heritage/archive",
    image: "https://www.m2l-usa.com/wp-content/uploads/2017/11/1A_Houston_Crew_IMG_8049-min.jpg",
  },
  {
    icon: Award,
    title: "Milestones",
    description: "Achievements and recognitions earned throughout our journey.",
    link: "/heritage/timeline",
    image: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=600",
  },
];

const legacyTeam = [
  {
    name: "Lisete Neto",
    role: "Executive Director",
    image: "https://www.m2l-usa.com/wp-content/uploads/2017/11/lieste.jpg",
  },
  {
    name: "Neusa Costa",
    role: "Academic Coordinator",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200",
  },
  {
    name: "Elizabeth Nhati",
    role: "Academic Advisor",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200",
  },
  {
    name: "Vania Gaspar",
    role: "Accounting Officer",
    image: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=200",
  },
];

const HeritagePage = () => {
  return (
    <main className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{
              backgroundImage: 'url("https://www.m2l-usa.com/wp-content/uploads/2017/11/1A_Houston-headquarters-city-skyline-min-1024x576.jpg")',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060B19]/80 via-[#060B19]/90 to-[#060B19]" />
        </div>

        {/* Vintage overlay */}
        <div 
          className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <Archive className="w-6 h-6 text-secondary" />
              <span className="text-secondary text-sm font-medium tracking-[0.2em] uppercase">
                Heritage Archive
              </span>
            </div>

            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Our Story, Our{" "}
              <span className="text-secondary">Legacy</span>
            </h1>

            <p className="text-xl text-white/60 leading-relaxed mb-8">
              Welcome to the M2L Heritage Archive — a digital museum preserving our 
              history, celebrating our achievements, and honoring the foundation of 
              trust that built our global network.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/heritage/timeline"
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-[#060B19] rounded-lg font-semibold hover:bg-secondary/90 transition-all group"
              >
                <Clock className="w-5 h-5" />
                View Timeline
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/heritage/archive"
                className="inline-flex items-center gap-2 px-6 py-3 border border-secondary/30 text-secondary rounded-lg font-medium hover:bg-secondary/10 transition-all"
              >
                <Archive className="w-5 h-5" />
                Browse Archive
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Heritage Cards Grid */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Explore Our Heritage
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Navigate through different aspects of our organizational history and legacy content.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {heritageCards.map((card, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={card.link}
                  className="group block h-full"
                >
                  <div className="relative h-full rounded-2xl overflow-hidden glass-card">
                    {/* Image */}
                    <div className="h-48 overflow-hidden">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="w-12 h-12 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center mb-4">
                        <card.icon className="w-6 h-6 text-secondary" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white mb-2 group-hover:text-secondary transition-colors">
                        {card.title}
                      </h3>
                      <p className="text-white/60 text-sm">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy Video Section */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-secondary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
                From Our Archives
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Watch Our Story Unfold
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                In March 2013, Management to Learn Ltd., a UK-based company managing 
                academic programs for international students, opened its first US branch 
                in Houston to support the growing number of students pursuing education 
                in America.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                Our fundamental purpose has always been to offer high-quality services 
                when managing academic programs for international students and executives 
                seeking to further their education abroad.
              </p>
              <a
                href="https://vimeo.com/243340335"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-secondary hover:underline"
              >
                <Play className="w-5 h-5" />
                Watch Legacy Video on Vimeo
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden glass-card">
                <img
                  src="https://www.m2l-usa.com/wp-content/uploads/2017/10/College-students-e1510941833125.jpg"
                  alt="M2L Legacy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <a
                    href="https://vimeo.com/243340335"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-20 h-20 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center hover:bg-secondary/30 transition-all"
                  >
                    <Play className="w-8 h-8 text-secondary ml-1" />
                  </a>
                </div>
              </div>
              {/* Vintage frame decoration */}
              <div className="absolute -inset-4 border border-secondary/20 rounded-3xl pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founding Team */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              The People Behind Our Mission
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our Leadership Team
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The dedicated professionals who built and continue to guide our organization.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {legacyTeam.map((member, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="relative mb-4 mx-auto w-32 h-32">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full rounded-full object-cover border-2 border-secondary/30"
                  />
                  <div className="absolute inset-0 rounded-full border-2 border-secondary/20 scale-110" />
                </div>
                <h4 className="font-semibold text-white">{member.name}</h4>
                <p className="text-sm text-secondary">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence Legacy */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our Global Footprint
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Present on three continents, managing student experiences across 100+ cities worldwide.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {["USA", "Angola", "Canada", "Panama", "France", "Portugal", "Poland", "Germany", "UK"].map((country, idx) => (
              <motion.div
                key={country}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="px-6 py-3 rounded-full glass-card border border-secondary/20"
              >
                <span className="text-white/80 font-medium">{country}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-secondary/10 via-transparent to-secondary/10 border-y border-secondary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Be Part of Our Future?
          </h2>
          <p className="text-white/60 mb-8">
            Our heritage is the foundation. Our future is being written now — with you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all"
            >
              Explore Current Services
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white rounded-lg font-medium hover:bg-white/10 transition-all"
            >
              Connect With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HeritagePage;
